/**
 * @file `Account` — namespacer for `lab.account.*` covering the
 *       `/account/*` routes + AccountMenu surface in lab's top nav.
 * @module @labcraft/lab/src/client/account
 *
 * Per the workspace Client-shape rule (`.agents/rules/services.md` §
 * "Workspace Client shape — one `CoreClient`, lifecycle namespacers"):
 * the parent `Lab` is the workspace's single `CoreClient`; `Account`
 * is a `CoreLifecycle` namespacer that bubbles logs/metrics under
 * `lab.account.*`. It holds no transport of its own — the parent
 * adopts the shared `HttpTransport` and passes a reference here at
 * construction.
 *
 * **Class name collision** with the loopback `Account` Client from
 * `@labcraft/account` — every callsite that imports both aliases
 * (`import { Account as LoopbackAccount } from '@labcraft/account'`);
 * the lab `Account` keeps the bare name per ADR-010's
 * package-name-as-class-name convention.
 */

import { CoreLifecycle } from '@labcraft/core'
import type { Acquisition } from '@labcraft/core'
import { BrowserAcquisition, browserTimezone } from '@labcraft/core/frontend'
import type { HttpTransport } from './index.ts'
import {
  EmailSigninRoute,
  SignoutRoute,
  type EmailSigninRequest,
  type SentResponse,
} from '@labcraft/account/proxy-routes'
import {
  CompleteChapterRoute,
  MeBudgetRoute,
  MeRoute,
  RecordObjectiveCompleteRoute,
  RecordPartAssessmentRoute,
  SetChapterRoute,
  UpdateAccountRoute,
  type BudgetResponse,
  type RecordObjectiveCompleteRequest,
  type RecordPartAssessmentRequest,
} from '../shared/account_routes.ts'
import type { ChapterRef } from '../shared/ws_messages.ts'

////////////////////////////////////////////////////////////////////////////////
// Types
////////////////////////////////////////////////////////////////////////////////

/**
 * Authenticated-learner view as the frontend uses it. Mirrors the
 * `MeRoute` response shape but flattens `emailVerifiedAt` /
 * `profileCompletedAt` to Date (or null) for downstream consumers.
 *
 * @property id - UUID primary key.
 * @property email - Lowercased email address.
 * @property displayName - Free-form display name.
 * @property emailVerifiedAt - Set when the magic link was clicked;
 *   null for unverified accounts.
 * @property currentChapter - Account-global chapter pointer (ADR-006
 *   (g)). Null when the learner hasn't navigated to a chapter yet —
 *   `/courses` falls back to the registry's `defaultChapter()`.
 * @property status - Access state. Drives the `AuthGuard`'s
 *   routing decision (inactive → /account/inactive, banned → /,
 *   active → through).
 * @property profileCompletedAt - Set when the learner first POSTs to
 *   `/account/update`. Null until then. Gates access to `/courses/*`
 *   on top of `status === 'active'`.
 * @property avatarUrl - Provider-hosted profile picture URL (Google
 *   `picture`, GitHub `avatar_url`) or null for email-mode learners.
 *   The topbar falls back to initials when null.
 * @property acquisition - Captured acquisition attribution (`source` +
 *   `category`), or null when never captured. The SPA flattens it into
 *   `umami.identify()` (umbrella #1213, Phase 2).
 * @property createdAt - The learner's signup instant, parsed to a `Date`.
 *   The SPA buckets it to a `YYYY-Www` cohort week for `umami.identify()`
 *   (umbrella #1213, Phase 2) — the raw timestamp never reaches Umami,
 *   only the derived bucket. Non-null: every `learners` row has a
 *   `created_at`.
 */
export type AccountLearner = {
  id: string
  email: string
  displayName: string
  emailVerifiedAt: Date | null
  createdAt: Date
  currentChapter: ChapterRef | null
  status: 'active' | 'inactive' | 'banned'
  profileCompletedAt: Date | null
  avatarUrl: string | null
  acquisition: Acquisition | null
}

/**
 * Wire-shape response from `/api/account/me`. Anonymous is a
 * payload value (`{ learner: null }`), not an error — every
 * page-load reads this and branches accordingly.
 */
export type MeResponse = {
  learner: AccountLearner | null
}

////////////////////////////////////////////////////////////////////////////////
// Account namespacer
////////////////////////////////////////////////////////////////////////////////

/**
 * Stateless typed namespacer for the lab `/api/account/*` HTTP surface.
 * Every method round-trips the daemon; identity caching is the `qkMe`
 * TanStack entry's job (`useMe()`); the 401 → cache flip lives in
 * `main.tsx`.
 */
export class Account extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport

  constructor(http: HttpTransport) {
    super({ id: 'account' })
    this.http = http
  }

  //////////////////////////////////////////////////////////////////////////////
  // Public
  //////////////////////////////////////////////////////////////////////////////

  /**
   * `GET /api/account/me` — returns the authenticated learner, or
   * `{ learner: null }` for an anonymous request. Dates are
   * flattened to `Date | null` here so consumers don't re-parse on
   * every read.
   */
  async me(): Promise<MeResponse> {
    const res = await this.http.request(MeRoute)
    if (!res.learner) return { learner: null }
    return {
      learner: {
        id: res.learner.id,
        email: res.learner.email,
        displayName: res.learner.displayName,
        emailVerifiedAt: res.learner.emailVerifiedAt
          ? new Date(res.learner.emailVerifiedAt)
          : null,
        createdAt: new Date(res.learner.createdAt),
        currentChapter: res.learner.currentChapter,
        status: res.learner.status,
        profileCompletedAt: res.learner.profileCompletedAt
          ? new Date(res.learner.profileCompletedAt)
          : null,
        avatarUrl: res.learner.avatarUrl,
        acquisition: res.learner.acquisition,
      },
    }
  }

  /**
   * `GET /api/account/me/budget` — the session learner's daily VM-time
   * budget (`tier` / `usedMsToday` / `limitMs`) plus the "go unlimited"
   * `upgradeUrl`. Drives the chrome budget meter (#941): `tier` gates
   * the meter's presence (paid → none), the used/limit pair drives the
   * remaining-time ring. The concrete local reset time is computed
   * SPA-side from the clock — it isn't on the wire.
   */
  async budget(): Promise<BudgetResponse> {
    return this.http.request(MeBudgetRoute)
  }

  /**
   * Mail a magic-link auth token to `email`. Sign-up and sign-in
   * collapse to the same call — account-service creates a fresh
   * `learners` row on first sighting (display name defaults to the
   * email's local-part; the learner fills the real value in at
   * `/account/update` post-approval), and logs in an existing
   * learner on subsequent calls.
   *
   * Always resolves with `{ status: 'sent' }` regardless of whether
   * the address exists — the silent-`sent` shape blocks enumeration
   * leaks.
   *
   * @param args - Email + optional `next` redirect target. Server
   *   sanitizes `next`.
   */
  async signinWithEmail(args: EmailSigninRequest): Promise<SentResponse> {
    return this.http.request(EmailSigninRoute, { body: args })
  }

  /**
   * Start a Google auth flow. Navigates the browser to lab's
   * `/api/account/google/authorize` endpoint; lab 302s to Google,
   * Google 302s back to `/api/account/google/callback`, and lab
   * mints the session cookie + 302s to `next`. Sign-up and sign-in
   * resolve to the same entry point.
   *
   * Returns void — the browser navigates away on
   * `window.location.assign`.
   *
   * Touches `window` directly: the only method on any UI Client that
   * does. SW-readiness specs route through HTTP methods only; this
   * one isn't covered by the no-DOM-globals invariant by design.
   *
   * @param next - Optional same-origin redirect target the learner
   *   lands at after auth completes. Server-side sanitized.
   */
  signinWithGoogle(next?: string): void {
    const url = new URL('/api/account/google/authorize', window.location.origin)
    if (next) url.searchParams.set('next', next)
    const tz = browserTimezone()
    if (tz) url.searchParams.set('tz', tz)
    // Classified acquisition — the browser classifies referrer + utm at
    // capture (#1214) into a granular open `source` + a coarse `category`,
    // read from the persisted first-touch value (or classified live), so
    // only bounded `acq_source` / `acq_cat` tokens ride the query params,
    // never a raw referrer/utm string.
    const acq = BrowserAcquisition.read()
    if (acq?.source) url.searchParams.set('acq_source', acq.source)
    if (acq?.category) url.searchParams.set('acq_cat', acq.category)
    if (acq?.campaign) url.searchParams.set('acq_campaign', acq.campaign)
    window.location.assign(url.toString())
  }

  /**
   * Start a GitHub auth flow. Mirrors `signinWithGoogle` — navigates
   * the browser to lab's `/api/account/github/authorize` endpoint; lab
   * 302s to GitHub, GitHub 302s back to
   * `/api/account/github/callback`, and lab mints the session cookie
   * + 302s to `next`. Sign-up and sign-in resolve to the same entry
   * point.
   *
   * @param next - Optional same-origin redirect target the learner
   *   lands at after auth completes. Server-side sanitized.
   */
  signinWithGithub(next?: string): void {
    const url = new URL('/api/account/github/authorize', window.location.origin)
    if (next) url.searchParams.set('next', next)
    const tz = browserTimezone()
    if (tz) url.searchParams.set('tz', tz)
    // Classified acquisition — see `signinWithGoogle`. Only the bounded
    // `acq_source` / `acq_cat` tokens ride the query params, never a raw
    // referrer/utm string.
    const acq = BrowserAcquisition.read()
    if (acq?.source) url.searchParams.set('acq_source', acq.source)
    if (acq?.category) url.searchParams.set('acq_cat', acq.category)
    if (acq?.campaign) url.searchParams.set('acq_campaign', acq.campaign)
    window.location.assign(url.toString())
  }

  /**
   * Sign out — clears the cookie + Redis session. The caller's
   * `useSignoutMutation.onSuccess` flips `qkMe` to
   * `{ learner: null }` synchronously to avoid a chrome-bounce.
   */
  async signout(): Promise<void> {
    await this.http.request(SignoutRoute)
  }

  /**
   * Update the learner-mutable profile fields. Returns the
   * server's freshly-stamped learner so the caller's
   * `useUpdateProfileMutation.onSuccess` can write it straight into
   * `qkMe`.
   *
   * Throws via the underlying transport on 4xx/5xx — the caller is
   * expected to render an inline error (the SPA's `AuthGuard` already
   * prevents waitlisted learners from reaching the form, so a 403
   * here means the local state is stale and a fresh `useMe()`
   * refetch should follow).
   */
  async updateProfile(args: { displayName: string }): Promise<AccountLearner> {
    const res = await this.http.request(UpdateAccountRoute, {
      body: args,
    })
    return {
      id: res.learner.id,
      email: res.learner.email,
      displayName: res.learner.displayName,
      emailVerifiedAt: res.learner.emailVerifiedAt
        ? new Date(res.learner.emailVerifiedAt)
        : null,
      createdAt: new Date(res.learner.createdAt),
      currentChapter: res.learner.currentChapter,
      status: res.learner.status,
      profileCompletedAt: res.learner.profileCompletedAt
        ? new Date(res.learner.profileCompletedAt)
        : null,
      avatarUrl: res.learner.avatarUrl,
      acquisition: res.learner.acquisition,
    }
  }

  /**
   * Persist a new account-global chapter pointer (ADR-006 (g)).
   * Posts the ref to `POST /account/me/chapter`; lab updates
   * `learner_progress` subject to the umbrella #1203 soft gate.
   *
   * Returns whether the write was **gated** — `true` when lab held the
   * advance back because the target's assessment-gated predecessor isn't
   * passed yet (the pointer was left unchanged). The caller's
   * `useSetChapterMutation.onSuccess` patches
   * `qkMe.learner.currentChapter` **only when not gated**, so a held-
   * back advance doesn't leave the cache claiming a pointer the server
   * never wrote. The cross-tab `chapter` broadcast so sibling tabs
   * follow is an unbuilt follow-up (#1060 retired the producer-less
   * `chapter` frame + `onChapter` consumer).
   *
   * @param ref - the chapter pointer to advance to
   * @returns `true` iff the soft gate held the advance back
   */
  async setChapter(ref: ChapterRef): Promise<boolean> {
    const res = await this.http.request(SetChapterRoute, { body: ref })
    return res.gated ?? false
  }

  /**
   * Record the learner's terminal Part-level assessment pass (umbrella
   * #1357). Posts to `POST /account/me/part-assessment`; lab upserts
   * `learner_part_assessment`. The Part-assessment page calls this on the
   * CAT's `pass` verdict so a subsequent `setChapter` advance into a Part
   * that lists this Part as a prerequisite clears the soft gate.
   * Idempotent — a re-pass refreshes the row.
   *
   * @param pass - the passed Part (`courseId` + `partId`) + the weighted
   *   `score` banked at pass time + the optional terminal CAT `band`
   */
  async recordPartAssessment(pass: RecordPartAssessmentRequest): Promise<void> {
    await this.http.request(RecordPartAssessmentRoute, { body: pass })
  }

  /**
   * `POST /account/me/objective-complete` — record the learner's completion
   * of one chapter objective (#1493). Idempotent: a re-run of the satisfying
   * command records nothing new. The reader fires this fire-and-forget on
   * each `objective.completed` so a VM resume / reload can rehydrate the
   * chapter's checkmarks.
   *
   * @param completion - the objective's location (`courseId` + `partId` +
   *   `chapterId`) plus its authored `objectiveId`
   */
  async recordObjectiveComplete(
    completion: RecordObjectiveCompleteRequest,
  ): Promise<void> {
    await this.http.request(RecordObjectiveCompleteRoute, { body: completion })
  }

  /**
   * Record an authoritative, blocker-proof chapter completion (#1218).
   * Posts the ref to `POST /account/me/chapter/complete`; lab fires the
   * server-origin `progress_chapter_complete` Umami event with the
   * learner UUID as the distinct id. Called alongside the client-side
   * `track('reader_chapter_complete')` — the two are a deliberate pair,
   * and the server one survives content blockers.
   *
   * Fire-and-forget from the caller's view: the response is an empty ack;
   * the actual Umami send is best-effort server-side. Errors are the
   * caller's to swallow (a failed completion beat must never break the
   * reader), mirroring the `track()` no-op-on-absent-tracker contract.
   */
  async completeChapter(ref: ChapterRef): Promise<void> {
    await this.http.request(CompleteChapterRoute, { body: ref })
  }
}
